import { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Input, Spin, Select, DatePicker } from "antd";
import DoctorSelect from "../../../../../Components/Dropdowns/DoctorSelect";
import NormalValueInput from "../../../../../Components/report/NormalValueInput";
import TestUnitInput from "../../../../../Components/report/TestUnitInput";
import { toast } from "react-toastify";
import { usePrintReport } from "../../../../../Components/PrintReport";
import Sidebar from "../../GlobalFiles/Sidebar";
import PrintTestReport from "../../../../../Components/PrintReport/PrintTestReport";
import isTextTypeReport from "../../../../../helper/isTextTypeReport";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Eye,
  Trash2,
  Calendar,
  User,
  Phone,
  Heart,
  DollarSign,
  Clock,
  ChevronLeft,
  ChevronRight,
  FileText,
  UserCheck,
  Building,
  Filter,
  Edit,
  Workflow,
} from "lucide-react";
import moment from "moment";
import { API_BASE_URL } from "../../../../../api";
import { BiComment } from "react-icons/bi";
import { GiPlagueDoctorProfile } from "react-icons/gi";

const ManageReports = () => {
  const { printReport } = usePrintReport();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("created");
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const navigate = useNavigate();
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({});
  const itemsPerPage = 10;

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/reports?depth=2`);
      console.log("Fetched reports:", response.data);
      setReports(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Error loading reports: " + (error.message || "Unknown error"));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const deleteReport = async (reportId) => {
    try {
      await axios.delete(`${API_BASE_URL}/reports/${reportId}`);
      toast.success("Report deleted successfully");
      setReports((prev) => prev.filter((r) => r._id !== reportId));
      setIsConfirmDeleteOpen(false);
      setReportToDelete(null);
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    }
  };

  const handlePrintReport = (report) => {
    printReport(report);
  };

  const filteredReports = reports?.filter((r) => {
    const q = searchQuery.toLowerCase();
    const patientName = r.patient?.patientName || r.patientName || "";
    const patientMobile = r.patient?.mobile || r.patientMobile || "";
    const email = r.patient?.email || r.email || "";
    const doctorName = r.doctor?.docName || r.docName || "";
    const docDepartment = r.doctor?.department || r.docDepartment || "";
    const patientID = String(r.patient?.patientID || r.patientID || "");

    return (
      patientID.toLowerCase().includes(q) ||
      patientName.toLowerCase().includes(q) ||
      email.toLowerCase().includes(q) ||
      String(patientMobile).includes(q) ||
      doctorName.toLowerCase().includes(q) ||
      docDepartment.toLowerCase().includes(q)
    );
  });

  const sortedReports = filteredReports?.sort((a, b) => {
    if (sortBy === "schedule") {
      return new Date(a.date) - new Date(b.date);
    } else {
      const dateA = new Date(a.createdAt || a._id);
      const dateB = new Date(b.createdAt || b._id);
      return dateB - dateA;
    }
  });

  const totalPages = Math.ceil((sortedReports?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReports = sortedReports?.slice(startIndex, endIndex);

  const handleViewDetails = (report) => {
    setSelectedReport(report);
    setIsModalVisible(true);
  };

  const handleEditClick = (report) => {
    const testOrderObj = typeof report.testOrder === 'object' && report.testOrder
      ? { ...report.testOrder, report: report }
      : { _id: report.testOrder, report: report, patient: report.patient };
    navigate('/report/add', { state: { testOrder: testOrderObj, report } });
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedReport(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Status color based on computed status
  const getStatusColor = (order) => {
    const status = order?.status || (order?.results?.every(r => r.status === 'completed') ? 'completed' : 'pending');
    if (status === 'completed') return "bg-emerald-100 text-emerald-700";
    return "bg-blue-100 text-blue-700";
  };

  return (
    <>
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar onCollapse={setSidebarCollapsed} />
        <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`} style={{overflowX: 'hidden'}}>
          <div className="flex-1 p-6 ml-40">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Calendar className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">Manage Reports</h1>
                        <p className="text-gray-600">View and manage ( update or modify ) all reports</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-2">
                      <Search className="w-5 h-5 text-gray-400" />
                      <Input
                        placeholder="Search by name, email, mobile, doctor, or agent"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border-none bg-transparent focus:ring-0 focus:border-none"
                        style={{ width: 300, boxShadow: "none" }}
                      />
                    </div>
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-2">
                      <Filter className="w-5 h-5 text-gray-400" />
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="border-none bg-transparent focus:ring-0 text-gray-700"
                      >
                        <option value="schedule">Sort by Schedule</option>
                        <option value="created">Sort by Created</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-blue-300/90 rounded-lg shadow-sm border border-gray-200 p-6 ">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-700 text-sm">Total Reports</p>
                      <p className="text-2xl font-bold text-gray-900">{reports?.length || 0}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <Calendar className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>
                <div className="bg-green-300/90 rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-700 text-sm">Filtered Results</p>
                      <p className="text-2xl font-bold text-gray-900">{filteredReports?.length || 0}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <Search className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>
                <div className="bg-purple-300/90 rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-700 text-sm">Current Page</p>
                      <p className="text-2xl font-bold text-gray-900">{currentPage} of {totalPages}</p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-full">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Spin size="large" />
                    <p className="mt-4 text-gray-600">Loading reports...</p>
                  </div>
                ) : (
                  <>
                    {currentReports?.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16">
                        <div className="p-4 bg-gray-100 rounded-full mb-4">
                          <Calendar className="w-12 h-12 text-gray-400" />
                        </div>
                        <p className="text-gray-600 text-lg">No reports found</p>
                        <p className="text-gray-500 text-sm mt-2">Try adjusting your search criteria</p>
                      </div>
                    ) : (
                      <>
                        <div className="hidden md:block overflow-x-auto" style={{maxWidth: '100vw'}}>
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left p-4 font-semibold text-gray-700">Patient</th>
                                <th className="text-left p-4 font-semibold text-gray-700">Contact</th>
                                <th className="text-left p-4 font-semibold text-gray-700">Doctor</th>
                                <th className="text-left p-4 font-semibold text-gray-700">Doctor Department</th>
                                <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                                <th className="text-left p-4 font-semibold text-gray-700">Created At</th>
                                <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              { currentReports?.map((report) => (
                                <tr
                                  key={report._id}
                                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                  onClick={()=> handleViewDetails(report) }
                                >
                                  <td className="p-4">
                                    <div className="flex items-center gap-3">
                                      <div className="p-2 bg-blue-100 rounded-full">
                                        <User className="w-4 h-4 text-blue-600" />
                                      </div>
                                      <div>
                                        <p className="text-sm text-gray-500">Order ID: {report.testOrder?.testOrderID || report.testOrderID || 'N/A'}</p>
                                        <p className="font-medium text-gray-900">{report.patient?.patientName || report.patientName}</p>
                                        <p className="text-sm text-gray-600">{report.patient?.age || report.patientAge} years, {report.patient?.gender || report.patientGender}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="p-4">
                                    <p>{ report.patient?.email || report.email }</p>
                                    <p>{ report.patient?.mobile || report.patientMobile }</p>
                                  </td>
                                  <td className="p-4">
                                    <div className="flex items-center gap-2">
                                      <UserCheck className="w-4 h-4 text-green-500" />
                                      <span className="text-sm text-gray-700">{report.doctor?.docName || report.docName || "N/A"}</span>
                                    </div>
                                  </td>
                                  <td className="p-4 text-center">{ report.doctor?.department || report.docDepartment }</td>
                                  <td className="p-4">
                                    {(() => {
                                      const st = (report.status || (report.results?.length > 0 && report.results.every(r => r.status === 'completed') ? 'completed' : 'pending'))?.toLowerCase();
                                      return st === 'completed' ? (
                                        <span className="px-2.5 py-1 inline-flex items-center rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200 capitalize">
                                          Completed
                                        </span>
                                      ) : (
                                        <span className="px-2.5 py-1 inline-flex items-center rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 capitalize">
                                          Pending
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td className="p-4">
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-blue-500" />
                                        <span className="text-sm text-gray-700">{report.date ? new Date(report.date).toLocaleDateString() : 'N/A'}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-purple-500" />
                                        <span className="text-sm text-gray-700">{report.createdAt ? new Date(report.createdAt).toLocaleDateString() : 'N/A'}</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="p-4">
                                    <div className="grid grid-cols-2 gap-2">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleViewDetails(report); }}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors text-sm font-medium"
                                      >
                                        <Eye className="w-4 h-4" />
                                        View
                                      </button>

                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleEditClick(report); }}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors text-sm font-medium"
                                      >
                                        <Edit className="w-4 h-4" />
                                        Edit
                                      </button>
                                      {/* <button
                                        onClick={(e) => { e.stopPropagation(); handlePrintReport(report); }}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors text-sm font-medium"
                                      >
                                        <FileText className="w-4 h-4" />
                                        Print
                                      </button> */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setReportToDelete(report);
                                          setIsConfirmDeleteOpen(true);
                                        }}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors text-sm font-medium"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="md:hidden">
                          {currentReports?.map((report) => (
                            <div
                              key={report._id}
                              className="p-4 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-blue-100 rounded-full">
                                    <User className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-medium text-gray-900">{report.patient?.patientName || report.patientName}</h3>
                                    <p className="text-sm text-gray-600">{report.patient?.age || report.patientAge} years, {report.patient?.gender || report.patientGender}</p>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleViewDetails(report)}
                                    className="p-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handlePrintReport(report)}
                                    className="p-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition-colors"
                                  >
                                    <FileText className="w-4 h-4" />
                                  </button>
                                  <PrintTestReport order={report} />
                                  <button
                                    onClick={() => {
                                      setReportToDelete(report);
                                      setIsConfirmDeleteOpen(true);
                                    }}
                                    className="p-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-700">{report.patient?.mobile || report.patientMobile}</span>
                                </div>
                                {/* <div className="flex items-center gap-2">
                                  <Heart className="w-4 h-4 text-red-500" />
                                  <span className="text-sm text-gray-700">{report.patientDisease || "N/A"}</span>
                                </div> */}
                                <div className="flex items-center gap-2">
                                  <Building className="w-4 h-4 text-indigo-500" />
                                  <span className="text-sm text-gray-700">{report.agentName || "N/A"}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm text-gray-700">{report.date ? new Date(report.date).toLocaleDateString() : 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-500">Status:</span>
                                  {(() => {
                                    const st = (report.status || (report.results?.length > 0 && report.results.every(r => r.status === 'completed') ? 'completed' : 'pending'))?.toLowerCase();
                                    return st === 'completed' ? (
                                      <span className="px-2.5 py-0.5 inline-flex items-center rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200 capitalize">
                                        Completed
                                      </span>
                                    ) : (
                                      <span className="px-2.5 py-0.5 inline-flex items-center rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 capitalize">
                                        Pending
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {totalPages > 1 && (
                          <div className="p-4 border-t border-gray-200 bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-700">Showing {startIndex + 1} to {Math.min(endIndex, sortedReports?.length || 0)} of {sortedReports?.length || 0} results</div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={handlePreviousPage}
                                  disabled={currentPage === 1}
                                  className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <ChevronLeft className="w-4 h-4" />
                                  Previous
                                </button>
                                <div className="flex gap-1">
                                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                      key={page}
                                      onClick={() => handlePageChange(page)}
                                      className={`px-3 py-2 rounded-lg transition-colors ${currentPage === page ? "bg-blue-600 text-white" : "bg-white border border-gray-300 hover:bg-gray-50"}`}
                                    >
                                      {page}
                                    </button>
                                  ))}
                                </div>
                                <button
                                  onClick={handleNextPage}
                                  disabled={currentPage === totalPages}
                                  className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Next
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>


            <Modal
              title={
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Report Details</h3>
                    <p className="text-sm text-gray-600">Complete report information</p>
                  </div>
                </div>
              }
              open={isModalVisible}
              onCancel={handleCloseModal}
              footer={[
                <Button
                  key="delete"
                  onClick={() => {
                    setReportToDelete(selectedReport);
                    setIsConfirmDeleteOpen(true);
                    setIsModalVisible(false);
                  }}
                  className="bg-red-100 hover:bg-red-200 border-red-300 text-red-700"
                >
                  Delete Report
                </Button>,
                <Button
                  key="edit"
                  onClick={() => {
                    handleEditClick(selectedReport);
                    setIsModalVisible(false);
                  }}
                  className="bg-green-100 hover:bg-green-200 border-green-300 text-green-700"
                >
                  Edit Report
                </Button>,
                <Button
                  key="back"
                  onClick={handleCloseModal}
                  className="bg-gray-100 hover:bg-gray-200 border-gray-300"
                >
                  Close
                </Button>,
              ]}
              width={800}
              className="custom-modal"
            >
              {selectedReport && (
                <div className="space-y-6 pt-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <User className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-900">Patient Information</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium text-gray-900">{selectedReport.patient?.patientName || selectedReport.patientName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Age:</span>
                          <span className="font-medium text-gray-900">{selectedReport.patient?.age || selectedReport.patientAge}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Gender:</span>
                          <span className="font-medium text-gray-900">{selectedReport.patient?.gender || selectedReport.patientGender}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mobile:</span>
                          <span className="font-medium text-gray-900">{selectedReport.patient?.mobile || selectedReport.patientMobile}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium text-gray-900">{selectedReport.patient?.email || selectedReport.email || "N/A"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Address:</span>
                          <span className="font-medium text-gray-900">{selectedReport.patient?.address || selectedReport.address || "N/A"}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Heart className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-gray-900">Medical Information</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* <div className="flex justify-between">
                        <span className="text-gray-600">Disease:</span>
                        <span className="font-medium text-gray-900">{selectedReport.patientDisease || "N/A"}</span>
                      </div> */}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Doctor:</span>
                        <span className="font-medium text-gray-900">{selectedReport.doctor?.docName || selectedReport.docName || "N/A"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Doctor Department:</span>
                        <span className="font-medium text-gray-900">{selectedReport.doctor?.department || selectedReport.docDepartment || "N/A"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-5 h-5 text-purple-600" />
                      <h4 className="font-semibold text-gray-900">Tests' Results Information</h4>
                    </div>
                    {selectedReport.results && selectedReport.results.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-purple-100">
                              <th className="text-left p-2 font-medium text-gray-700">#</th>
                              <th className="text-left p-2 font-medium text-gray-700">Test Name</th>
                              <th className="text-left p-2 font-medium text-gray-700">Value</th>
                              <th className="text-left p-2 font-medium text-gray-700">Unit</th>
                              <th className="text-left p-2 font-medium text-gray-700">Normal</th>
                              <th className="text-left p-2 font-medium text-gray-700">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedReport.results.map((res, index) => {
                              const isTextType = isTextTypeReport(res.test?.testGroup?.superGroup);
                              return (
                                <tr key={index} className="border-t border-purple-200">
                                  <td className="p-2 text-gray-900">{index + 1}</td>
                                  <td className="p-2 text-gray-900">{res.test?.title || res.test?.testName || 'N/A'}</td>
                                  <td className="p-2 text-gray-900 whitespace-pre-wrap" colSpan={isTextType ? 3 : 1}>
                                    {res.value || '-'}
                                  </td>
                                  {!isTextType && (
                                    <>
                                      <td className="p-2 text-gray-900">{res.testUnit || '-'}</td>
                                      <td className="p-2 text-gray-900">{res.normalValue || '-'}</td>
                                    </>
                                  )}
                                  <td className="p-2 text-gray-900">{res.status || '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-600">No tests information available</p>
                    )}
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-5 h-5 text-indigo-600" />
                      <h4 className="font-semibold text-gray-900">Order Time</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium text-gray-900">{selectedReport.date ? new Date(selectedReport.date).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time:</span>
                        <span className="font-medium text-gray-900">{selectedReport.time || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-medium text-gray-900 ${getStatusColor(selectedReport)}`}>{selectedReport.status || (selectedReport.results?.every(r=>r.status==='completed') ? 'completed' : 'pending')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Modal>

            <Modal
              title={
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                </div>
              }
              open={isConfirmDeleteOpen}
              onCancel={() => {
                setIsConfirmDeleteOpen(false);
                setReportToDelete(null);
              }}
              footer={[
                <Button
                  key="cancel"
                  onClick={() => {
                    setIsConfirmDeleteOpen(false);
                    setReportToDelete(null);
                  }}
                  className="bg-gray-100 hover:bg-gray-200 border-gray-300"
                >
                  Cancel
                </Button>,
                <Button
                  key="delete"
                  onClick={() => deleteReport(reportToDelete?._id)}
                  className="bg-red-100 hover:bg-red-200 border-red-300 text-red-700"
                >
                  Delete
                </Button>,
              ]}
              width={400}
              className="custom-modal"
            >
              <div className="space-y-4">
                <p className="text-gray-600 text-center">Are you sure you want to delete the report for <span className="font-semibold">{reportToDelete?.patient?.patientName || reportToDelete?.patientName}</span>?</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm font-medium text-center">⚠️ This action cannot be undone.</p>
                </div>
              </div>
            </Modal>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageReports;